import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Select from "../../components/common/Select";
import Alert from "../../components/common/Alert";
import PageHeader from "../../components/common/PageHeader";
import Section from "../../components/common/Section";
import Table from "../../components/common/Table";
import {
  useGetUserQuery,
  useGetUserRolesQuery,
  useGetRolesQuery,
  useCreateUserRoleMutation,
  useDeleteUserRoleMutation,
} from "../../store/api";

const UserRoleAssignment = () => {
  const { userId } = useParams();
  const navigate = useNavigate();

  const { data: user } = useGetUserQuery(userId);
  const { data: userRoles, isLoading: loadingUserRoles } =
    useGetUserRolesQuery(userId);
  const { data: allRoles } = useGetRolesQuery({});
  const [createUserRole, createState] = useCreateUserRoleMutation();
  const [deleteUserRole] = useDeleteUserRoleMutation();

  const [form, setForm] = useState({
    roleId: "",
    businessId: "",
    storeId: "",
  });

  const roles = Array.isArray(allRoles) ? allRoles : [];
  const assignedRoles = Array.isArray(userRoles) ? userRoles : [];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        roleId: form.roleId,
        businessId: form.businessId || null,
        storeId: form.storeId || null,
      };
      await createUserRole({ userId, payload }).unwrap();
      setForm({ roleId: "", businessId: "", storeId: "" });
    } catch (err) {
      // error handled by UI
    }
  };

  const handleDelete = async (userRoleId) => {
    if (window.confirm("Remove this role from the user?")) {
      try {
        await deleteUserRole({ userId, userRoleId }).unwrap();
      } catch (err) {
        // error handled by UI
      }
    }
  };

  const columns = [
    { key: "role", label: "Role" },
    { key: "scope", label: "Scope" },
    { key: "business", label: "Business ID" },
    { key: "store", label: "Store ID" },
    { key: "actions", label: "Actions" },
  ];

  const rows = assignedRoles.map((userRole) => {
    const id = userRole.userRoleId || userRole.id;
    const role = roles.find((r) => (r.roleId || r.id) === userRole.roleId);
    const roleName = role?.name || role?.roleName || userRole.roleId;

    let scopeText;
    if (userRole.storeId) {
      scopeText = "Store";
    } else if (userRole.businessId) {
      scopeText = "Business";
    } else {
      scopeText = "Global";
    }

    return {
      id,
      role: roleName,
      scope: scopeText,
      business: userRole.businessId || "-",
      store: userRole.storeId || "-",
      actions: (
        <button
          type="button"
          className="text-sm font-semibold text-rose-600"
          onClick={() => handleDelete(id)}
        >
          Remove
        </button>
      ),
    };
  });

  const availableRoles = roles.filter(
    (role) =>
      !assignedRoles.some(
        (ur) => ur.roleId === role.roleId || ur.roleId === role.id,
      ),
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Assign Roles: ${user?.displayName || user?.email || "User"}`}
        subtitle="Manage role assignments with optional business/store scope."
      />

      <Card>
        <h2 className="mb-4 text-lg font-semibold text-slate-900">
          Assign New Role
        </h2>
        <form className="space-y-4" onSubmit={handleSubmit}>
          <Section title="Role Assignment">
            <div className="grid gap-4 md:grid-cols-2">
              <Select
                label="Role"
                name="roleId"
                value={form.roleId}
                onChange={handleChange}
                required
              >
                <option value="">Select a role</option>
                {availableRoles.map((role) => (
                  <option
                    key={role.roleId || role.id}
                    value={role.roleId || role.id}
                  >
                    {role.name || role.roleName}
                  </option>
                ))}
              </Select>
              <Input
                label="Business ID (Optional)"
                name="businessId"
                placeholder="Leave empty for global scope"
                value={form.businessId}
                onChange={handleChange}
                helper="Restrict role to specific business"
              />
              <Input
                label="Store ID (Optional)"
                name="storeId"
                placeholder="Leave empty for business/global scope"
                value={form.storeId}
                onChange={handleChange}
                helper="Restrict role to specific store"
              />
            </div>
          </Section>

          {createState.isSuccess && (
            <Alert tone="success">Role assigned successfully.</Alert>
          )}
          {createState.isError && (
            <Alert tone="error">
              {createState.error?.data?.detail || "Failed to assign role."}
            </Alert>
          )}

          <Button type="submit" disabled={createState.isLoading}>
            Assign Role
          </Button>
        </form>
      </Card>

      <Card className="p-0">
        <div className="border-b border-slate-100 px-6 py-4 text-sm font-semibold text-slate-700">
          Current Role Assignments
        </div>
        <div className="px-6 py-4">
          {loadingUserRoles && (
            <p className="text-sm text-slate-500">Loading roles...</p>
          )}
          {!loadingUserRoles && (
            <Table
              columns={columns}
              rows={rows}
              emptyText="No roles assigned yet."
            />
          )}
        </div>
      </Card>

      <Card>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={() => navigate("/users")}>
            Back to Users
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default UserRoleAssignment;
